import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Tooltip,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import RolesPermissionsEditor from './RolesPermissionsEditor';
import { styled } from '@mui/material/styles';
import { getCurrentEnvironment } from '../config/environment';
import api from '../services/api';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(2),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '8px 16px',
  marginRight: theme.spacing(1),
}));

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => ({
  borderRadius: 16,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: status === 'active' ? '#C6F6D5' : 
                   status === 'suspended' ? '#FED7D7' : 
                   status === 'pending_verification' ? '#FEEBC8' : '#E2E8F0',
  color: status === 'active' ? '#22543D' : 
         status === 'suspended' ? '#742A2A' : 
         status === 'pending_verification' ? '#744210' : '#4A5568',
}));

// Types
interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  status: string;
  user_type: string;
  roles: string[];
  email_verified: boolean;
  last_login: string | null;
  created_at: string;
  updated_at: string;
}

interface Role {
  id: string;
  name: string;
  display_name: string;
  description: string;
  is_system_role: boolean;
  is_default: boolean;
  permissions: string[];
  created_at: string;
}

interface Permission {
  id: string;
  name: string;
  display_name: string;
  description: string;
  module: string;
  action: string;
  resource: string;
  created_at: string;
}

interface EnvironmentInfo {
  environment: string;
  dev_mode: boolean;
  proptrak_api_base: string;
  aletheia_database_configured: boolean;
  proptrak_database_configured: boolean;
}

const AdminUserManagement: React.FC = () => {
  // State
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [operationLoading, setOperationLoading] = useState<{ [key: string]: boolean }>({});
  const [lastDataRefresh, setLastDataRefresh] = useState<Date | null>(null);
  
  // Dialog states
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [environmentDialogOpen, setEnvironmentDialogOpen] = useState(false);

  const [showRolesEditor, setShowRolesEditor] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ open: boolean; userId: string | null; userName: string }>({
    open: false,
    userId: null,
    userName: ''
  });
  
  // Form states
  const [userForm, setUserForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    display_name: '',
    password: '',
    user_type: 'internal',
    status: 'pending_verification',
    email_verified: false,
    roles: [] as string[],
  });

  // API base URL - use environment configuration
  const API_BASE = getCurrentEnvironment().aletheiaAdminApi;

  // Load data
  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading data from API_BASE:', API_BASE);
      
      const [usersData, rolesData, permissionsData, envData] = await Promise.all([
        api.get<{ users: User[] }>('/api/admin/users'),
        api.get<{ roles: Role[] }>('/api/admin/roles'),
        api.get<{ permissions: Permission[] }>('/api/admin/permissions'),
        api.get<EnvironmentInfo>('/api/admin/environment')
      ]);

      console.log('API responses: Success');

      setUsers(usersData.users || []);
      setRoles(rolesData.roles || []);
      setPermissions(permissionsData.permissions || []);
      setEnvironmentInfo(envData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(`Network error: ${errorMessage}`);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
      setLastDataRefresh(new Date());
    }
  };

  // User operations
  const handleCreateUser = async () => {
    try {
      const responseData = await api.post<{ user: User; message: string }>('/api/admin/users', userForm);
        
        // Add the new user to local state immediately for better UX
        if (responseData.user) {
          const newUser: User = {
            id: responseData.user.id,
            email: userForm.email,
            first_name: userForm.first_name,
            last_name: userForm.last_name,
            display_name: userForm.display_name,
            status: userForm.status,
            user_type: userForm.user_type,
            roles: userForm.roles,
            email_verified: userForm.email_verified,
            last_login: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setUsers(prevUsers => [newUser, ...prevUsers]);
        }

        setSuccess('User created successfully');
        setUserDialogOpen(false);
        resetUserForm();
        
        // Also refresh from server to ensure consistency
        setTimeout(() => {
          loadData();
        }, 500);
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error instanceof Error ? error.message : 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    const operationKey = `update_${editingUser.id}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userForm),
      });

      if (response.ok) {
        // Update the local state immediately for better UX
        const updatedUsers = users.map(user => {
          if (user.id === editingUser.id) {
            return {
              ...user,
              first_name: userForm.first_name,
              last_name: userForm.last_name,
              display_name: userForm.display_name,
              status: userForm.status,
              user_type: userForm.user_type,
              email_verified: userForm.email_verified,
              roles: userForm.roles,
              updated_at: new Date().toISOString()
            };
          }
          return user;
        });
        setUsers(updatedUsers);

        // Create a more descriptive success message
        const updatedFields = [];
        if (editingUser.first_name !== userForm.first_name) updatedFields.push('first name');
        if (editingUser.last_name !== userForm.last_name) updatedFields.push('last name');
        if (editingUser.display_name !== userForm.display_name) updatedFields.push('display name');
        if (editingUser.status !== userForm.status) updatedFields.push('status');
        if (editingUser.user_type !== userForm.user_type) updatedFields.push('user type');
        if (editingUser.email_verified !== userForm.email_verified) updatedFields.push('email verification');
        if (JSON.stringify(editingUser.roles.sort()) !== JSON.stringify(userForm.roles.sort())) updatedFields.push('roles');
        
        const successMessage = updatedFields.length > 0 
          ? `User updated successfully. Changed: ${updatedFields.join(', ')}`
          : 'User updated successfully';
        
        setSuccess(successMessage);
        setUserDialogOpen(false);
        setEditingUser(null);
        resetUserForm();
        
        // Also refresh from server to ensure consistency
        setTimeout(() => {
          loadData();
        }, 500);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update user');
      }
    } catch (err) {
      setError('Failed to update user');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  const handleDeleteUser = async (userId: string) => {
    const operationKey = `delete_${userId}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Remove user from local state immediately
        setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
        setSuccess('User deleted successfully');
        
        // Also refresh from server to ensure consistency
        setTimeout(() => {
          loadData();
        }, 500);
      } else {
        setError('Failed to delete user');
      }
    } catch (err) {
      setError('Failed to delete user');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  const handleSuspendUser = async (userId: string) => {
    const operationKey = `suspend_${userId}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${userId}/suspend`, {
        method: 'POST',
      });

      if (response.ok) {
        // Update user status in local state immediately
        setUsers(prevUsers => prevUsers.map(user => 
          user.id === userId ? { ...user, status: 'suspended' } : user
        ));
        setSuccess('User suspended successfully');
        
        // Also refresh from server to ensure consistency
        setTimeout(() => {
          loadData();
        }, 500);
      } else {
        setError('Failed to suspend user');
      }
    } catch (err) {
      setError('Failed to suspend user');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  const handleActivateUser = async (userId: string) => {
    const operationKey = `activate_${userId}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${userId}/activate`, {
        method: 'POST',
      });

      if (response.ok) {
        // Update user status in local state immediately
        setUsers(prevUsers => prevUsers.map(user => 
          user.id === userId ? { ...user, status: 'active' } : user
        ));
        setSuccess('User activated successfully');
        
        // Also refresh from server to ensure consistency
        setTimeout(() => {
          loadData();
        }, 500);
      } else {
        setError('Failed to activate user');
      }
    } catch (err) {
      setError('Failed to activate user');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  // Form handlers
  const resetUserForm = () => {
    setUserForm({
      email: '',
      first_name: '',
      last_name: '',
      display_name: '',
      password: '',
      user_type: 'internal',
      status: 'pending_verification',
      email_verified: false,
      roles: [],
    });
  };

  const openUserDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        display_name: user.display_name,
        password: '',
        user_type: user.user_type,
        status: user.status,
        email_verified: user.email_verified,
        roles: user.roles,
      });
    } else {
      setEditingUser(null);
      resetUserForm();
    }
    setUserDialogOpen(true);
  };

  const openDeleteConfirmDialog = (userId: string, userName: string) => {
    setDeleteConfirmDialog({
      open: true,
      userId,
      userName
    });
  };

  const closeUserDialog = () => {
    setUserDialogOpen(false);
    setEditingUser(null);
    resetUserForm();
  };

  // Render functions
  const renderUserDialog = () => (
    <Dialog open={userDialogOpen} onClose={closeUserDialog} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingUser ? 'Edit User' : 'Create New User'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="Email"
            value={userForm.email}
            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
            disabled={!!editingUser}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={userForm.password}
            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
            helperText={editingUser ? 'Leave blank to keep current password' : ''}
          />
          <TextField
            fullWidth
            label="First Name"
            value={userForm.first_name}
            onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Last Name"
            value={userForm.last_name}
            onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Display Name"
            value={userForm.display_name}
            onChange={(e) => setUserForm({ ...userForm, display_name: e.target.value })}
          />
          <FormControl fullWidth>
            <InputLabel>User Type</InputLabel>
            <Select
              value={userForm.user_type}
              onChange={(e) => setUserForm({ ...userForm, user_type: e.target.value })}
              label="User Type"
            >
              <MenuItem value="internal">Internal</MenuItem>
              <MenuItem value="external">External</MenuItem>
              <MenuItem value="system">System</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={userForm.status}
              onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending_verification">Pending Verification</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={userForm.email_verified}
                onChange={(e) => setUserForm({ ...userForm, email_verified: e.target.checked })}
              />
            }
            label="Email Verified"
          />
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={userForm.roles}
                onChange={(e) => setUserForm({ ...userForm, roles: e.target.value as string[] })}
                label="Roles"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.name}>
                    {role.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeUserDialog}>Cancel</Button>
        <Button 
          onClick={editingUser ? handleUpdateUser : handleCreateUser}
          variant="contained"
          color="primary"
          disabled={editingUser ? operationLoading[`update_${editingUser.id}`] : false}
          startIcon={editingUser && operationLoading[`update_${editingUser.id}`] ? (
            <CircularProgress size={16} />
          ) : undefined}
        >
          {editingUser ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEnvironmentDialog = () => (
    <Dialog open={environmentDialogOpen} onClose={() => setEnvironmentDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Environment Configuration</DialogTitle>
      <DialogContent>
        {environmentInfo && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Current Environment</Typography>
                <Typography variant="body2" color="text.secondary">
                  Environment: {environmentInfo.environment}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Development Mode: {environmentInfo.dev_mode ? 'Enabled' : 'Disabled'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PropTrak API: {environmentInfo.proptrak_api_base}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Database Status</Typography>
                <Typography variant="body2" color="text.secondary">
                  Aletheia Database: {environmentInfo.aletheia_database_configured ? '✅ Connected' : '❌ Not Connected'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PropTrak Database: {environmentInfo.proptrak_database_configured ? '✅ Connected' : '❌ Not Connected'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEnvironmentDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A202C' }}>
          Admin User Management
        </Typography>
        <Box>
          <Tooltip title="Environment Settings">
            <IconButton onClick={() => setEnvironmentDialogOpen(true)}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <ActionButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openUserDialog()}
            sx={{ ml: 1 }}
          >
            Add User
          </ActionButton>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" color="primary">
              {users.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Users
            </Typography>
          </CardContent>
        </StyledCard>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" color="success.main">
              {users.filter(u => u.status === 'active').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Users
            </Typography>
          </CardContent>
        </StyledCard>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" color="warning.main">
              {users.filter(u => u.status === 'suspended').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Suspended Users
            </Typography>
          </CardContent>
        </StyledCard>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" color="info.main">
              {roles.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Roles
            </Typography>
          </CardContent>
        </StyledCard>
      </Box>



      {/* Users Table */}
      <StyledCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">
                Users
              </Typography>
              {lastDataRefresh && (
                <Typography variant="caption" color="text.secondary">
                  Last refreshed: {lastDataRefresh.toLocaleTimeString()}
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              onClick={loadData}
              disabled={loading}
              startIcon={loading ? <RefreshIcon /> : <RefreshIcon />}
              sx={{ 
                animation: loading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.display_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {user.first_name} {user.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          display: 'inline-block', 
                          backgroundColor: 'grey.100', 
                          px: 1, 
                          py: 0.25, 
                          borderRadius: 1,
                          fontSize: '0.7rem',
                          fontWeight: 500
                        }}>
                          {user.user_type}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Updated: {new Date(user.updated_at).toLocaleDateString()} {new Date(user.updated_at).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <StatusChip
                        label={user.status.replace('_', ' ')}
                        status={user.status}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {user.roles.map((role) => (
                          <Chip key={role} label={role} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit User">
                          <IconButton 
                            size="small" 
                            onClick={() => openUserDialog(user)}
                            disabled={operationLoading[`update_${user.id}`]}
                          >
                            {operationLoading[`update_${user.id}`] ? (
                              <CircularProgress size={16} />
                            ) : (
                              <EditIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        {user.status === 'active' ? (
                          <Tooltip title="Suspend User">
                            <IconButton 
                              size="small" 
                              onClick={() => handleSuspendUser(user.id)}
                              disabled={operationLoading[`suspend_${user.id}`]}
                            >
                              {operationLoading[`suspend_${user.id}`] ? (
                                <CircularProgress size={16} />
                              ) : (
                                <BlockIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Activate User">
                            <IconButton 
                              size="small" 
                              onClick={() => handleActivateUser(user.id)}
                              disabled={operationLoading[`activate_${user.id}`]}
                            >
                              {operationLoading[`activate_${user.id}`] ? (
                                <CircularProgress size={16} />
                              ) : (
                                <CheckCircleIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete User">
                          <IconButton 
                            size="small" 
                            onClick={() => openDeleteConfirmDialog(user.id, user.display_name || `${user.first_name} ${user.last_name}`)}
                            disabled={operationLoading[`delete_${user.id}`]}
                          >
                            {operationLoading[`delete_${user.id}`] ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </StyledCard>

      {/* Roles Section */}
      <StyledCard sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Roles & Permissions
            </Typography>
            <ActionButton
              variant="contained"
              startIcon={<SecurityIcon />}
              onClick={() => setShowRolesEditor(true)}
            >
              Edit Roles & Permissions
            </ActionButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {roles.map((role) => (
              <Card key={role.id} variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={500}>
                    {role.display_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {role.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {role.permissions.slice(0, 3).map((permission) => (
                      <Chip key={permission} label={permission} size="small" />
                    ))}
                    {role.permissions.length > 3 && (
                      <Chip label={`+${role.permissions.length - 3} more`} size="small" />
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </CardContent>
      </StyledCard>

      {/* Dialogs */}
      {renderUserDialog()}
      {renderEnvironmentDialog()}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, userId: null, userName: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the user <strong>{deleteConfirmDialog.userName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The user will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmDialog({ open: false, userId: null, userName: '' })}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (deleteConfirmDialog.userId) {
                handleDeleteUser(deleteConfirmDialog.userId);
                setDeleteConfirmDialog({ open: false, userId: null, userName: '' });
              }
            }}
            color="error"
            variant="contained"
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Roles & Permissions Editor Dialog */}
      <Dialog 
        open={showRolesEditor} 
        onClose={() => setShowRolesEditor(false)} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Roles & Permissions Editor</Typography>
            <IconButton onClick={() => setShowRolesEditor(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <RolesPermissionsEditor />
        </DialogContent>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUserManagement; 